import { FC, memo, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import clsx from 'clsx';
import './AnimatedText.scss';

interface AnimatedTextProps {
  text: string;
  className?: string;
}

const MIN_RENDERING_DURATION_IN_MS = 1000;

const AnimatedText: FC<AnimatedTextProps> = memo(
  ({ text, className }) => {
    const containerRef = useRef(null);
    const isInView = useInView(containerRef, { once: false });

    if (!text) return null;
    const splittedText = text.split('');

    const pullupVariant = {
      initial: { opacity: 0 },
      animate: (i: number) => ({
        opacity: 1,
        transition: {
          duration: MIN_RENDERING_DURATION_IN_MS / 1000,
          delay: i * 0.05,
          repeat: Infinity,
          repeatType: 'loop' as const,
          repeatDelay: 0,
        },
      }),
    };

    return (
      <div ref={containerRef} className="animated-text-container">
        {splittedText.map((current, i) => (
          <motion.div
            key={`${text}-${i}`}
            variants={pullupVariant}
            initial="initial"
            animate="animate"
            custom={i}
            className={clsx('animated-text', className)}
          >
            {current === ' ' ? <span>&nbsp;</span> : current}
          </motion.div>
        ))}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.text === nextProps.text;
  }
);

export default AnimatedText;
